/**
 * File: src/components/AthleteList.js
 * Description: Roster lineup listing, filtering data grid wrapper, and transaction dispatch controller.
 * Feeds data layers securely down to AthleteForm and handles asynchronous deletions/updates.
 * Version: 3.2.0
 *
 * @format
 */

import { useState } from '@wordpress/element';
import Swal from 'sweetalert2';
import AthleteForm from './AthleteForm.js';

const AthleteList = ({ athletes = [], meta = {}, onRefresh }) => {
	const [isModalOpen, setIsModalOpen] = useState(false);
	const [editingAthlete, setEditingAthlete] = useState(null);

	// Helper to format date to "Nov 10, 2016"
	const formatDate = (dateString) => {
		if (!dateString) return 'N/A';
		const date = new Date(dateString);
		return new Intl.DateTimeFormat('en-US', {
			month: 'short',
			day: 'numeric',
			year: 'numeric',
			timeZone: 'UTC',
		}).format(date);
	};

	// Computes specific division strings matching includes/amm-helpers.php logic
	const getLiveClassifications = (dob, sexCode) => {
		if (!dob) return { intClass: '—', hsClass: '—' };

		const parts = dob.split('-');
		if (parts.length !== 3) return { intClass: '—', hsClass: '—' };

		const bYear = parseInt(parts[0], 10);
		const bMonth = parseInt(parts[1], 10);
		const bDay = parseInt(parts[2], 10);

		const currentYear = new Date().getFullYear();

		// 1. INTL Calculation
		const intAge = currentYear - bYear;
		let intClass = 'Senior';
		if (intAge < 14) intClass = 'Under 14';
		else if (intAge === 14) intClass = 'Under 15';
		else if (intAge >= 15 && intAge <= 16) intClass = 'Under 17';
		else if (intAge === 17) intClass = 'Under 18';
		else if (intAge >= 18 && intAge <= 19) intClass = 'Under 20';

		// 2. High School Calculation
		let septemberAge = currentYear - bYear;
		if (bMonth > 9 || (bMonth === 9 && bDay > 0)) {
			septemberAge--;
		}

		let hsClass = 'Senior';
		const isFemale = sexCode?.toUpperCase() === 'F';

		if (isFemale) {
			if (septemberAge <= 13) hsClass = 'Class 4';
			else if (septemberAge <= 15) hsClass = 'Class 3';
			else if (septemberAge <= 17) hsClass = 'Class 2';
			else if (septemberAge <= 19) hsClass = 'Class 1';
		} else {
			if (septemberAge <= 14) hsClass = 'Class 3';
			else if (septemberAge <= 16) hsClass = 'Class 2';
			else if (septemberAge <= 19) hsClass = 'Class 1';
		}

		return { intClass, hsClass };
	};

	const handleOpenAddModal = () => {
		setEditingAthlete(null);
		setIsModalOpen(true);
	};

	const handleOpenEditModal = (athlete) => {
		setEditingAthlete(athlete);
		setIsModalOpen(true);
	};

	const handleCloseModal = () => {
		setIsModalOpen(false);
		setEditingAthlete(null);
	};

	const handleSave = (formData) => {
		let url, method;

		method = editingAthlete ? 'PUT' : 'POST';
		url = editingAthlete ? `${ammData.api_url}/athletes/${editingAthlete.id}` : `${ammData.api_url}/athletes`;

		fetch(url, {
			method: method,
			headers: { 'Content-Type': 'application/json', 'X-WP-Nonce': ammData.nonce },
			body: JSON.stringify(formData),
		})
			.then((res) => {
				if (!res.ok) throw new Error('Network save operation failed.');
				return res.json();
			})
			.then(() => {
				onRefresh();
				handleCloseModal();

				if (typeof Swal !== 'undefined') {
					Swal.fire({
						title: 'Saved Successfully!',
						text: editingAthlete ? 'The athlete record has been updated successfully.' : 'The profile record has been registered into your roster lineup.',
						icon: 'success',
						confirmButtonColor: '#0d6efd',
					});
				}
			})
			.catch((err) => {
				console.error('Save Athlete Error:', err);
				if (typeof Swal !== 'undefined') {
					Swal.fire({
						title: 'Submission Error',
						text: 'An unexpected processing issue occurred while attempting to save entry credentials.',
						icon: 'error',
						confirmButtonColor: '#0d6efd',
					});
				}
			});
	};

	const handleDelete = (id) => {
		if (typeof Swal === 'undefined') {
			fetch(`${ammData.api_url}/athletes/${id}`, {
				method: 'DELETE',
				headers: { 'X-WP-Nonce': ammData.nonce },
			})
				.then(() => onRefresh())
				.catch((err) => console.error('Delete Athlete Error:', err));
			return;
		}

		Swal.fire({
			title: 'Are you sure?',
			text: 'This will permanently remove this athlete profile from your team records!',
			icon: 'warning',
			showCancelButton: true,
			confirmButtonColor: '#dc3545',
			cancelButtonColor: '#6c757d',
			confirmButtonText: 'Yes, delete it!',
			cancelButtonText: 'Cancel',
		}).then((result) => {
			if (result.isConfirmed) {
				fetch(`${ammData.api_url}/athletes/${id}`, {
					method: 'DELETE',
					headers: { 'X-WP-Nonce': ammData.nonce },
				})
					.then((res) => {
						if (!res.ok) throw new Error('Delete response execution failure.');
						onRefresh();
						Swal.fire({
							title: 'Deleted!',
							text: 'The selected roster profile has been permanently scrubbed.',
							icon: 'success',
							confirmButtonColor: '#0d6efd',
						});
					})
					.catch((err) => {
						console.error('Delete Athlete Error:', err);
						Swal.fire({
							title: 'Error',
							text: 'Failed to process resource removal request context parameters.',
							icon: 'error',
							confirmButtonColor: '#0d6efd',
						});
					});
			}
		});
	};

	const individualAthletes = athletes.filter((a) => !a.isRelay && !a.relayName);

	return (
		<div className="advanced-mm-athlete-entries-wrapper bg-white rounded shadow-sm border p-3">
			{/* Header Actions Row */}
			<div className="d-flex flex-wrap justify-content-between align-items-center gap-3 mb-3 border-bottom pb-2">
				<h5 className="fw-bold text-dark mb-0">
					<i className="fas fa-running me-2 text-primary"></i>
					Athletes ({individualAthletes.length})
				</h5>

				<button className="btn btn-primary btn-sm d-flex align-items-center gap-2 shadow-sm" onClick={handleOpenAddModal}>
					<i className="fas fa-plus-circle"></i>
					Add Athlete
				</button>
			</div>

			{/* Empty State View Guarding */}
			{individualAthletes.length === 0 ? (
				<div className="alert alert-info shadow-sm p-4 text-center border-dashed">
					<i className="fas fa-user-clock fa-2x mb-2 text-muted"></i>
					<p className="m-0 fw-medium text-secondary">No registered club athletes found. Click above to populate rosters.</p>
				</div>
			) : (
				<div className="table-responsive">
					<table className="table table-hover align-middle mb-0 small">
						<thead className="table-light text-uppercase text-secondary">
							<tr>
								<th>Athlete</th>
								<th style={{ width: '15%' }}>Age Class</th>
								<th style={{ width: '15%' }}>DOB</th>
								<th>Affiliation</th>
								<th className="text-end" style={{ width: '100px' }}>
									Actions
								</th>
							</tr>
						</thead>
						<tbody>
							{individualAthletes.map((athlete) => {
								const currentSex = athlete.sexCode || athlete.gender || 'M';
								const classes = getLiveClassifications(athlete.dateOfBirth, currentSex);
								return (
									<tr key={athlete.id}>
										<td>
											<span className="fw-semibold text-dark">
												{athlete.firstName} {athlete.middleName || ''}
											</span>{' '}
											<span className="fw-bold text-uppercase text-dark">{athlete.lastName}</span>
											<div className="mt-0.5">
												<span className={`badge lh-sm ${currentSex === 'M' ? 'text-bg-primary bg-opacity-10 text-primary border-primary-subtle' : 'text-bg-danger bg-opacity-10 text-danger border-danger-subtle'} px-2 py-1`}>{currentSex === 'M' ? 'Male' : 'Female'}</span>
											</div>
										</td>
										<td>
											<div className="text-muted small">
												<strong>HS:</strong> <span className="badge bg-secondary bg-opacity-10 text-secondary border border-secondary border-opacity-20 lh-sm fw-semibold">{classes.hsClass}</span>
											</div>
											<div className="text-muted small">
												<strong>Intl:</strong> <span className="badge bg-secondary bg-opacity-10 text-secondary border border-secondary border-opacity-20 lh-sm fw-semibold">{classes.intClass}</span>
											</div>
										</td>
										<td className="text-secondary">{formatDate(athlete.dateOfBirth)}</td>
										<td>
											<span className="fw-medium text-secondary">{athlete.clubName || athlete.clubCode}</span>
											{athlete.countryCode && <span className="text-muted small ms-1">({athlete.countryCode})</span>}
										</td>
										<td className="text-end">
											<div className="btn-group">
												<button className="btn btn-sm btn-light border px-2 shadow-none" onClick={() => handleOpenEditModal(athlete)} title="Edit Competitor Profile">
													<i className="fas fa-edit text-secondary"></i>
												</button>
												<button className="btn btn-sm btn-light border px-2 shadow-none" onClick={() => handleDelete(athlete.id)} title="Delete Competitor Profile">
													<i className="fas fa-trash-alt text-danger"></i>
												</button>
											</div>
										</td>
									</tr>
								);
							})}
						</tbody>
					</table>
				</div>
			)}

			{/* Individual Athlete Modal Dialog */}
			{isModalOpen && (
				<div className="amm-modal-overlay animate-fade-in" style={{ backgroundColor: 'rgba(0,0,0,0.45)' }}>
					<div className="card shadow border-0 m-auto" style={{ maxWidth: '620px', width: '100%' }}>
						<div className="card-header bg-light border-bottom d-flex justify-content-between align-items-center py-3 px-4">
							<h5 className="mb-0 fw-bold text-dark">
								<i className={`fas ${editingAthlete ? 'fa-user-edit' : 'fa-user-plus'} me-2 text-secondary`}></i>
								{editingAthlete ? 'Modify Athlete Profile' : 'Register Individual Athlete'}
							</h5>
							<button className="btn-close shadow-none" onClick={handleCloseModal}></button>
						</div>
						<div className="card-body p-4">
							<AthleteForm onSave={handleSave} onCancel={handleCloseModal} initialData={editingAthlete ? { ...editingAthlete, _contextTab: 'individual' } : { isRelay: false, _contextTab: 'individual', dateOfBirth: '' }} clubs={meta.clubs || {}} countries={meta.countries || {}} />
						</div>
					</div>
				</div>
			)}
		</div>
	);
};

export default AthleteList;
